// =====================================================================
// EGL v12 Frontend - Certificate List Component
// Purpose: Main certificate list/export interface with column selector
// =====================================================================

import React, { useState, useEffect } from 'react';
import { useRouter } from '../lib/SmartRouter';
import { 
  CertificateListItem, 
  CertificateListResponse, 
  CertificateListFilters,
  ColumnConfig 
} from '../types/certificateList';
import { exportToExcel } from '../utils/excelExport';
import ConfirmDialog from './ConfirmDialog';
import './CertificateList.css';

const CertificateList: React.FC = () => {
  const { user, navigate } = useRouter();
  // State
  const [certificates, setCertificates] = useState<CertificateListItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCertificates, setSelectedCertificates] = useState<Set<string>>(new Set());
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; cert: CertificateListItem | null }>({
    isOpen: false,
    cert: null
  });
  
  // Zebra ODBC state
  const [zebraStatus, setZebraStatus] = useState<{
    lastPublished: string | null;
    publishedCount: number;
    publishedBy: string | null;
  }>({ lastPublished: null, publishedCount: 0, publishedBy: null });
  const [showZebraPreview, setShowZebraPreview] = useState<boolean>(false);
  const [zebraPublishing, setZebraPublishing] = useState<boolean>(false);
  const [zebraToast, setZebraToast] = useState<{ show: boolean; message: string; type: 'success' | 'error' }>({
    show: false,
    message: '',
    type: 'success'
  });

  // User location permissions
  const [userPermissions, setUserPermissions] = useState<{location: string; can_publish: boolean; can_cross_publish: boolean}[]>([]);
  const [userLocation, setUserLocation] = useState<string>('DUBAI');
  
  // Excel export password protection
  const [showExcelPasswordPrompt, setShowExcelPasswordPrompt] = useState<boolean>(false);
  const [excelPassword, setExcelPassword] = useState<string>('');
  const [excelPasswordError, setExcelPasswordError] = useState<string>('');
  
  // Column selector password protection
  const [showColumnSelectorPasswordPrompt, setShowColumnSelectorPasswordPrompt] = useState<boolean>(false);
  const [columnSelectorPassword, setColumnSelectorPassword] = useState<string>('');
  const [columnSelectorPasswordError, setColumnSelectorPasswordError] = useState<string>('');
  
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });
  
  // Filters (initialize with user's location if available)
  const [filters, setFilters] = useState<CertificateListFilters>({
    location: (user?.location as 'DUBAI' | 'DOHA' | 'ALL') || 'DUBAI',
    search: '',
    sortOrder: 'DESC',
    page: 1,
    limit: 20
  });
  
  // Check if user is admin
  const isAdmin = user?.rights === 'ADMIN';
  
  // Column visibility configuration (all 27 columns + checkbox + actions)
  const [columns, setColumns] = useState<ColumnConfig[]>([
    { key: 'select', label: '☑', enabled: true, alwaysVisible: true },
    { key: 'cert_id', label: 'Certificate ID', enabled: true, alwaysVisible: true },
    { key: 'created_at', label: 'Created', enabled: true },
    { key: 'template_name', label: 'Template', enabled: true },
    { key: 'location', label: 'Location', enabled: true },
    { key: 'username', label: 'User', enabled: true },
    { key: 'item_gross_weight', label: 'Item Gross Weight', enabled: true },
    { key: 'metal_composition', label: 'Metal Composition', enabled: true },
    { key: 'gemstone_growth_origin', label: 'Gemstone Growth Origins', enabled: true },
    { key: 'comment_line_1', label: 'Comment L1', enabled: true },
    { key: 'comment_line_2', label: 'Comment L2', enabled: true },
    { key: 'comment_line_3', label: 'Comment L3', enabled: true },
    { key: 'abbreviation_line_1', label: 'Abbreviation L1', enabled: true },
    { key: 'abbreviation_line_2', label: 'Abbreviation L2', enabled: true },
    { key: 'abbreviation_line_3', label: 'Abbreviation L3', enabled: true },
    { key: 'abbreviation_line_4', label: 'Abbreviation L4', enabled: true },
    { key: 'abbreviation_line_5', label: 'Abbreviation L5', enabled: true },
    { key: 'abbreviation_line_6', label: 'Abbreviation L6', enabled: true },
    { key: 'abbreviation_line_7', label: 'Abbreviation L7', enabled: true },
    { key: 'abbreviation_line_8', label: 'Abbreviation L8', enabled: true },
    { key: 'abbreviation_line_9', label: 'Abbreviation L9', enabled: true },
    { key: 'abbreviation_line_10', label: 'Abbreviation L10', enabled: true },
    { key: 'abbreviation_line_11', label: 'Abbreviation L11', enabled: true },
    { key: 'abbreviation_line_12', label: 'Abbreviation L12', enabled: true },
    { key: 'abbreviation_line_13', label: 'Abbreviation L13', enabled: true },
    { key: 'uurlid', label: 'UURLID', enabled: true },
    { key: 'public_url', label: 'Public URL', enabled: true },
    ...(isAdmin ? [{ key: 'actions' as const, label: 'Actions', enabled: true, alwaysVisible: true }] : [])
  ]);
  
  // Column selector visibility
  const [showColumnSelector, setShowColumnSelector] = useState<boolean>(false);
  
  // Fetch certificates
  const fetchCertificates = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Get token (ProtectedRoute already verified authentication)
      const token = localStorage.getItem('egl_token');
      if (!token) {
        setError('Session expired. Please refresh the page.');
        setLoading(false);
        return;
      }
      
      // Build query string
      const params = new URLSearchParams({
        page: filters.page.toString(),
        limit: filters.limit.toString(),
        sortOrder: filters.sortOrder,
        location: filters.location
      });
      
      if (filters.search) {
        params.append('search', filters.search);
      }
      
      const response = await fetch(`/api/certificates/list?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data: CertificateListResponse = await response.json();
      
      if (data.success) {
        setCertificates(data.data.certificates);
        setPagination(data.data.pagination);
      } else {
        setError(data.message || 'Failed to fetch certificates');
      }
      
    } catch (err) {
      console.error('Fetch error:', err);
      setError(err instanceof Error ? err.message : 'Failed to load certificates');
    } finally {
      setLoading(false);
    }
  };
  
  // ALWAYS reset location filter to user's registered location on mount
  // This ensures every visit to the page (including back navigation) resets the filter
  useEffect(() => {
    console.log('🚀 Component mounted - resetting location filter to user location');
    if (user?.location) {
      console.log(`📍 Setting location filter to: ${user.location}`);
      setFilters(prev => ({ 
        ...prev, 
        location: user.location as 'DUBAI' | 'DOHA' | 'ALL',
        page: 1 // Reset to page 1 on mount
      }));
    }
  }, []); // Empty deps = run ONCE on mount (every time component mounts)
  
  // Fetch permissions ONCE on mount
  useEffect(() => {
    console.log('🚀 Initial mount - fetching permissions');
    fetchUserPermissions();
  }, []); // Empty deps = run once on mount

  // Load on mount and when filters change
  useEffect(() => {
    fetchCertificates();
    fetchZebraStatus(); // Also fetch Zebra ODBC status
  }, [filters.page, filters.limit, filters.sortOrder, filters.location, filters.search]);
  
  // Auto-refresh Zebra ODBC status every 30 seconds (live updates)
  useEffect(() => {
    const interval = setInterval(() => {
      fetchZebraStatus();
    }, 30000); // 30 seconds
    
    return () => clearInterval(interval); // Cleanup on unmount
  }, []);
  
  // Toggle column visibility
  const toggleColumn = (key: string) => {
    console.log('toggleColumn called with key:', key);
    setColumns(prev => {
      const updated = prev.map(col => {
        if (col.key === key && !col.alwaysVisible) {
          console.log(`Toggling ${key} from ${col.enabled} to ${!col.enabled}`);
          return { ...col, enabled: !col.enabled };
        }
        return col;
      });
      console.log('Updated columns:', updated);
      return updated;
    });
  };
  
  // Toggle all columns
  const toggleAllColumns = (enabled: boolean) => {
    setColumns(prev => prev.map(col => {
      if (col.alwaysVisible) {
        return col;
      }
      return { ...col, enabled };
    }));
  };
  
  // Certificate selection handlers
  const toggleCertificate = (certId: string) => {
    setSelectedCertificates(prev => {
      const newSet = new Set(prev);
      if (newSet.has(certId)) {
        newSet.delete(certId);
      } else {
        newSet.add(certId);
      }
      return newSet;
    });
  };
  
  const toggleAllCertificates = () => {
    if (selectedCertificates.size === certificates.length) {
      setSelectedCertificates(new Set());
    } else {
      const allIds = certificates.map(c => c.cert_id);
      setSelectedCertificates(new Set(allIds));
    }
  };
  
  const isAllSelected = certificates.length > 0 && selectedCertificates.size === certificates.length;
  
  // Export to Excel (only selected certificates) - Show password prompt first
  const handleExportToExcel = () => {
    if (selectedCertificates.size === 0) {
      alert('Please select at least one certificate to export');
      return;
    }
    
    // Show password prompt
    setShowExcelPasswordPrompt(true);
    setExcelPassword('');
    setExcelPasswordError('');
  };
  
  // Perform actual Excel export after password verification
  const performExcelExport = () => {
    const EXPORT_PASSWORD = 'exportexcel';
    
    if (excelPassword !== EXPORT_PASSWORD) {
      setExcelPasswordError('❌ Incorrect password');
      return;
    }
    
    // Password correct - proceed with export
    const selectedCerts = certificates.filter(cert => selectedCertificates.has(cert.cert_id));
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    const filename = `egl_certificates_${filters.location}_${timestamp}.csv`;
    
    exportToExcel(selectedCerts, columns, filename);
    
    // Close modal and reset
    setShowExcelPasswordPrompt(false);
    setExcelPassword('');
    setExcelPasswordError('');
  };
  
  // Cancel Excel export
  const cancelExcelExport = () => {
    setShowExcelPasswordPrompt(false);
    setExcelPassword('');
    setExcelPasswordError('');
  };
  
  // Column Selector - Show password prompt first
  const handleColumnSelectorClick = () => {
    setShowColumnSelectorPasswordPrompt(true);
    setColumnSelectorPassword('');
    setColumnSelectorPasswordError('');
  };
  
  // Open column selector after password verification
  const openColumnSelector = () => {
    const SELECTOR_PASSWORD = 'selectorcolumn';
    
    if (columnSelectorPassword !== SELECTOR_PASSWORD) {
      setColumnSelectorPasswordError('❌ Incorrect password');
      return;
    }
    
    // Password correct - open column selector
    setShowColumnSelector(true);
    setShowColumnSelectorPasswordPrompt(false);
    setColumnSelectorPassword('');
    setColumnSelectorPasswordError('');
  };
  
  // Cancel column selector
  const cancelColumnSelector = () => {
    setShowColumnSelectorPasswordPrompt(false);
    setColumnSelectorPassword('');
    setColumnSelectorPasswordError('');
  };
  
  // Export to Microsoft Access (only selected certificates)
  const handleExportToAccess = async () => {
    if (selectedCertificates.size === 0) {
      alert('Please select at least one certificate to export');
      return;
    }

    try {
      // Show loading state
      setLoading(true);
      
      const token = localStorage.getItem('egl_token');
      if (!token) {
        setError('Session expired. Please refresh the page.');
        return;
      }

      // Get selected certificate IDs
      const selectedCertIds = certificates
        .filter(cert => selectedCertificates.has(cert.cert_id))
        .map(cert => cert.cert_id);

      // Get enabled columns
      const enabledColumns = columns
        .filter(col => col.enabled && col.key !== 'select' && col.key !== 'actions' && col.key !== 'index')
        .map(col => col.key as string);

      // Make request to backend
      const response = await fetch('/api/certificates/export-access', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          certificate_ids: selectedCertIds,
          columns: enabledColumns
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to generate Access database');
      }

      // Get filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
      const filename = filenameMatch ? filenameMatch[1] : `egl_certificates_${Date.now()}.mdb`;

      // Convert response to blob
      const blob = await response.blob();

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.style.display = 'none';
      
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      
      // Cleanup
      window.URL.revokeObjectURL(url);

      console.log(`✅ Access database exported: ${filename}`);

    } catch (err) {
      console.error('Access export error:', err);
      setError(err instanceof Error ? err.message : 'Failed to export to Access');
      alert(`Export failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };
  
  // =====================================================================
  // ZEBRA PRINT STUDIO ODBC FUNCTIONS
  // =====================================================================
  
  // Fetch current Zebra ODBC staging status
  // Fetch user's location permissions
  const fetchUserPermissions = async () => {
    try {
      const token = localStorage.getItem('egl_token');
      if (!token) {
        console.warn('⚠️ No token found for permissions fetch');
        return;
      }
      
      console.log('🔍 Fetching user permissions...');
      const response = await fetch('/api/zebra/permissions', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        console.log('📦 Permissions API response:', result);
        
        if (result.success && result.data) {
          setUserPermissions(result.data.permissions);
          setUserLocation(result.data.user_location);
          console.log('✅ User permissions loaded:', {
            userLocation: result.data.user_location,
            permissions: result.data.permissions,
            publishableLocations: result.data.permissions.filter((p: any) => p.can_publish)
          });
        } else {
          console.error('❌ Permissions API returned unsuccessful:', result);
        }
      } else {
        const errorText = await response.text();
        console.error('❌ Permissions API failed:', response.status, errorText);
      }
    } catch (error) {
      console.error('❌ Error fetching user permissions:', error);
    }
  };

  const fetchZebraStatus = async () => {
    try {
      const token = localStorage.getItem('egl_token');
      if (!token) return;
      
      const response = await fetch('/api/zebra/status', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setZebraStatus({
            lastPublished: result.data.last_published_at,
            publishedCount: result.data.total_certificates,
            publishedBy: result.data.last_published_by
          });
        }
      }
    } catch (error) {
      console.error('Error fetching Zebra status:', error);
    }
  };
  
  // State for target location
  const [targetPublishLocation, setTargetPublishLocation] = useState<'DUBAI' | 'DOHA'>('DUBAI');
  
  // Show publish preview modal with location
  const handlePublishToZebra = (location: 'DUBAI' | 'DOHA') => {
    if (selectedCertificates.size === 0) {
      alert('Please select at least one certificate to publish to ODBC');
      return;
    }
    setTargetPublishLocation(location);
    setShowZebraPreview(true);
  };
  
  // Actually publish to Zebra ODBC
  const confirmPublishToZebra = async () => {
    try {
      setZebraPublishing(true);
      setShowZebraPreview(false);
      
      const token = localStorage.getItem('egl_token');
      if (!token) {
        setZebraToast({ show: true, message: 'Session expired', type: 'error' });
        return;
      }
      
      // Get selected certificate UUIDs (not cert_id)
      const selectedCertIds = certificates
        .filter(cert => selectedCertificates.has(cert.cert_id))
        .map(cert => cert.id);
      
      // Get enabled columns
      const enabledColumns = columns
        .filter(col => col.enabled && col.key !== 'select' && col.key !== 'actions' && col.key !== 'index')
        .map(col => col.key as string);
      
      // Make API request
      const response = await fetch('/api/zebra/publish', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          certificateIds: selectedCertIds,
          selectedColumns: enabledColumns,
          location: targetPublishLocation // Send target location
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        // Update status
        await fetchZebraStatus();
        
        // Show success toast
        setZebraToast({
          show: true,
          message: `✅ Published ${result.data.published_count} certificates to ODBC`,
          type: 'success'
        });
        
        // Auto-hide success toast after 5 seconds
        setTimeout(() => {
          setZebraToast({ show: false, message: '', type: 'success' });
        }, 5000);
      } else {
        // Show error toast (stays on screen until manually dismissed)
        const errorMessage = result.hint 
          ? `❌ ${result.message}\n\n💡 ${result.hint}`
          : `❌ Failed: ${result.message}`;
        
        setZebraToast({
          show: true,
          message: errorMessage,
          type: 'error'
        });
      }
      
    } catch (error) {
      console.error('Zebra publish error:', error);
      // Show error toast (stays on screen until manually dismissed)
      setZebraToast({
        show: true,
        message: '❌ Network error. Please try again.',
        type: 'error'
      });
    } finally {
      setZebraPublishing(false);
    }
  };
  
  // Cancel publish
  const cancelPublishToZebra = () => {
    setShowZebraPreview(false);
  };
  
  // Format time ago (e.g., "5 minutes ago")
  const formatTimeAgo = (dateString: string | null) => {
    if (!dateString) return 'Never';
    
    const now = new Date();
    const past = new Date(dateString);
    const diffMs = now.getTime() - past.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };
  
  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Open public URL
  const openPublicUrl = (uurlid: string) => {
    window.open(`/${uurlid}`, '_blank');
  };
  
  // Handle delete certificate
  const handleDelete = async (cert: CertificateListItem) => {
    try {
      const token = localStorage.getItem('egl_token');
      if (!token) {
        setError('Session expired. Please refresh the page.');
        return;
      }
      
      // Use the certificate UUID (id) for deletion
      const response = await fetch(`/api/certificates/${cert.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to delete certificate');
      }
      
      // Refresh the certificate list
      await fetchCertificates();
      
      // Build detailed success message with S3 status
      let message = `✅ Certificate ${cert.cert_id} deleted successfully`;
      
      // Check S3 deletion status
      if (data.data?.image_filename) {
        if (data.data.s3_image_deleted) {
          message += `\n✅ Image removed from S3 storage (${data.data.image_filename})`;
        } else if (data.data.s3_error) {
          message += `\n⚠️  Warning: Image cleanup failed - ${data.data.s3_error}`;
          message += `\n   Image file: ${data.data.image_filename}`;
          message += `\n   You may need to manually delete this file from S3.`;
        } else {
          message += `\n⚠️  Warning: S3 deletion status unknown`;
          message += `\n   Image file: ${data.data.image_filename}`;
        }
      } else {
        message += `\n(No image to clean up)`;
      }
      
      // Show detailed success message
      alert(message);
      
    } catch (err) {
      console.error('Delete error:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete certificate');
    }
  };
  
  // Handle edit certificate
  const handleEdit = (cert: CertificateListItem) => {
    // Navigate to edit page (we'll create this route)
    navigate(`/certificates/edit/${cert.cert_id}`);
  };
  
  // Render cell value
  // Helper to check if user can publish cert to a location
  const canPublishCertToLocation = (certLocation: string, targetLocation: string) => {
    const perm = userPermissions.find(p => p.location === targetLocation);
    return perm?.can_publish && certLocation === targetLocation;
  };

  // Get selected certificates breakdown by location
  const getSelectedCertsByLocation = () => {
    const breakdown: { [key: string]: string[] } = {};
    certificates.forEach(cert => {
      if (selectedCertificates.has(cert.cert_id)) {
        if (!breakdown[cert.location]) {
          breakdown[cert.location] = [];
        }
        breakdown[cert.location].push(cert.cert_id);
      }
    });
    return breakdown;
  };

  const renderCell = (key: keyof CertificateListItem | 'select' | 'public_url' | 'actions', cert: CertificateListItem, index: number) => {
    if (key === 'select') {
      return (
        <div 
          className="checkbox-wrapper"
          onClick={(e) => {
            e.stopPropagation();
            toggleCertificate(cert.cert_id);
          }}
        >
          <input
            type="checkbox"
            checked={selectedCertificates.has(cert.cert_id)}
            readOnly
            className="cert-checkbox"
          />
        </div>
      );
    }
    
    if (key === 'public_url') {
      return (
        <button 
          className="btn-open-url"
          onClick={() => openPublicUrl(cert.uurlid)}
          title={`Open ${cert.uurlid}`}
        >
          🔗 Open
        </button>
      );
    }
    
    if (key === 'actions') {
      return (
        <div className="actions-cell">
          <button 
            className="btn-edit"
            onClick={() => handleEdit(cert)}
            title="Edit certificate"
          >
            ✏️ Edit
          </button>
          <button 
            className="btn-delete"
            onClick={() => setDeleteConfirm({ isOpen: true, cert })}
            title="Delete certificate"
          >
            🗑️ Delete
          </button>
        </div>
      );
    }
    
    if (key === 'created_at') {
      return formatDate(cert[key]);
    }
    
    const value = cert[key as keyof CertificateListItem];
    return value || '-';
  };
  
  // Get cell class based on column type
  const getCellClass = (key: keyof CertificateListItem | 'select' | 'public_url') => {
    const classes = [];
    
    // Sticky columns (stay visible when scrolling horizontally)
    if (key === 'select') {
      classes.push('cell-sticky', 'cell-sticky-select');
    }
    if (key === 'cert_id') {
      classes.push('cell-sticky', 'cell-sticky-cert-id');
    }
    
    // Comment lines need wide columns
    if (key.toString().startsWith('comment_line_')) {
      classes.push('cell-comment');
    }
    // Abbreviation lines can be narrower
    if (key.toString().startsWith('abbreviation_line_')) {
      classes.push('cell-abbreviation');
    }
    
    return classes.join(' ');
  };
  
  // Visible columns only
  const visibleColumns = columns.filter(col => col.enabled);
  
  return (
    <div className="certificate-list-container">
      {/* Header */}
      <div className="list-header">
        <div className="header-left">
          <h2>Certificate List</h2>
        </div>
        
        {/* Navigation Buttons - Top Right */}
        <div className="header-right">
          <button 
            className="btn-back-dashboard"
            onClick={() => navigate('/dashboard')}
            title="Back to Dashboard"
          >
            🏠 Dashboard
          </button>
          <button 
            className="btn-logout"
            onClick={() => {
              localStorage.removeItem('token');
              localStorage.removeItem('user');
              navigate('/login');
            }}
            title="Logout"
          >
            🚪 Logout
          </button>
        </div>
      </div>
      
      {/* Action Buttons and Status */}
      <div className="list-subheader">
        {/* Side-by-side layout: Buttons LEFT, Status RIGHT */}
        <div className="header-content">
          {/* Left Side: Button Grid */}
          <div className="header-actions">
            {/* First Row: Configuration & Export */}
            <div className="header-actions-row">
              <button 
                className="btn-action"
                onClick={handleColumnSelectorClick}
              >
                📋 Column Selector
              </button>
            </div>
            
            {/* Second Row: Export Actions */}
            <div className="header-actions-row">
              <button 
                className="btn-action btn-primary"
                onClick={handleExportToExcel}
                disabled={selectedCertificates.size === 0}
              >
                📊 Excel Export Selected ({selectedCertificates.size})
              </button>
              <button 
                className="btn-action btn-access"
                onClick={handleExportToAccess}
                disabled={true}
                title="Feature under development - Coming soon"
              >
                🗄️ Access Export Selected ({selectedCertificates.size})
              </button>
            </div>
            
            {/* Third Row: ODBC Publish Buttons - ALWAYS VISIBLE */}
            <div className="header-actions-row">
              {/* Dubai Button */}
              <button 
                className="btn-action btn-zebra btn-zebra-dubai"
                onClick={() => handlePublishToZebra('DUBAI')}
                disabled={
                  !userPermissions.find(p => p.location === 'DUBAI')?.can_publish || 
                  selectedCertificates.size === 0 || 
                  zebraPublishing
                }
                title={
                  !userPermissions.find(p => p.location === 'DUBAI')?.can_publish
                    ? '🔒 You don\'t have publish permission for DUBAI'
                    : selectedCertificates.size === 0
                    ? 'Select at least one certificate to publish'
                    : 'Publish selected certificates to DUBAI ODBC staging table'
                }
              >
                {zebraPublishing && targetPublishLocation === 'DUBAI' 
                  ? '⏳ Publishing...' 
                  : userPermissions.find(p => p.location === 'DUBAI')?.can_publish
                  ? `DUBAI: Publish to ODBC (${selectedCertificates.size})`
                  : `🔒 DUBAI: Publish to ODBC (${selectedCertificates.size})`}
              </button>

              {/* Doha Button */}
              <button 
                className="btn-action btn-zebra btn-zebra-doha"
                onClick={() => handlePublishToZebra('DOHA')}
                disabled={
                  !userPermissions.find(p => p.location === 'DOHA')?.can_publish || 
                  selectedCertificates.size === 0 || 
                  zebraPublishing
                }
                title={
                  !userPermissions.find(p => p.location === 'DOHA')?.can_publish
                    ? '🔒 You don\'t have publish permission for DOHA'
                    : selectedCertificates.size === 0
                    ? 'Select at least one certificate to publish'
                    : 'Publish selected certificates to DOHA ODBC staging table'
                }
              >
                {zebraPublishing && targetPublishLocation === 'DOHA' 
                  ? '⏳ Publishing...' 
                  : userPermissions.find(p => p.location === 'DOHA')?.can_publish
                  ? `DOHA: Publish to ODBC (${selectedCertificates.size})`
                  : `🔒 DOHA: Publish to ODBC (${selectedCertificates.size})`}
              </button>
            </div>

            {/* Selection Breakdown Warning */}
            {selectedCertificates.size > 0 && (() => {
              const breakdown = getSelectedCertsByLocation();
              const locations = Object.keys(breakdown);
              
              if (locations.length > 1) {
                return (
                  <div className="selection-warning">
                    ⚠️ <strong>Mixed locations selected:</strong> {locations.map(loc => `${breakdown[loc].length} ${loc}`).join(', ')}. 
                    <span className="warning-hint"> You can only publish certificates from one location at a time.</span>
                  </div>
                );
              }
              
              // Show info message for single location
              const loc = locations[0];
              const certIds = breakdown[loc];
              return (
                <div className="selection-info">
                  ℹ️ <strong>Selected:</strong> {certIds.length} {loc} certificate{certIds.length > 1 ? 's' : ''} 
                  ({certIds.slice(0, 3).join(', ')}{certIds.length > 3 ? `, +${certIds.length - 3} more` : ''})
                </div>
              );
            })()}
          </div>
          
          {/* Right Side: Zebra ODBC Status Panel - ALWAYS VISIBLE */}
          <div className="zebra-status-panel">
            <div className="zebra-status-header">
              <div className="zebra-status-title">
                <span className="zebra-status-icon">🖨️</span>
                <span>ODBC Staging Status</span>
              </div>
            </div>
            <div className="zebra-status-content">
              {zebraStatus.lastPublished ? (
                <>
                  <div className="zebra-status-item">
                    <span className="zebra-status-label">Published Certificates:</span>
                    <span className="zebra-status-value">{zebraStatus.publishedCount} certificate{zebraStatus.publishedCount !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="zebra-status-item">
                    <span className="zebra-status-label">Last Published:</span>
                    <span className="zebra-status-value">{formatTimeAgo(zebraStatus.lastPublished)}</span>
                  </div>
                  {zebraStatus.publishedBy && (
                    <div className="zebra-status-item">
                      <span className="zebra-status-label">Published By:</span>
                      <span className="zebra-status-value">{zebraStatus.publishedBy}</span>
                    </div>
                  )}
                  <div className="zebra-status-info">
                    <span className="zebra-info-icon">ℹ️</span>
                    <span>This data is currently available to Zebra Print Studio via ODBC connection.</span>
                  </div>
                </>
              ) : (
                <div className="zebra-status-empty">
                  <span className="zebra-empty-icon">📭</span>
                  <p>No data published yet</p>
                  <p className="zebra-empty-hint">Select certificates and click "Publish to ODBC"</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Column Selector */}
      {showColumnSelector && (
        <div className="column-selector">
          <div className="selector-header">
            <h3>Select Columns to Display/Export</h3>
            <div className="selector-actions">
              <button onClick={() => toggleAllColumns(true)}>✓ All</button>
              <button onClick={() => toggleAllColumns(false)}>✗ None</button>
              <button onClick={() => setShowColumnSelector(false)}>✕ Close</button>
            </div>
          </div>
          <div className="selector-grid">
            {columns.map(col => (
              <label 
                key={col.key} 
                className={col.alwaysVisible ? 'always-visible' : ''}
                onClick={(e) => {
                  e.preventDefault();
                  if (!col.alwaysVisible) {
                    console.log('Label clicked for:', col.key);
                    toggleColumn(col.key);
                  }
                }}
              >
                <input
                  type="checkbox"
                  checked={col.enabled}
                  disabled={col.alwaysVisible}
                  readOnly
                />
                <span>{col.label}</span>
                {col.alwaysVisible && <span className="badge">Required</span>}
              </label>
            ))}
          </div>
        </div>
      )}
      
      {/* Filters */}
      <div className="list-filters">
        <div className="filter-group">
          <label>Location:</label>
          <select
            value={filters.location}
            onChange={(e) => setFilters({ ...filters, location: (e.target as HTMLSelectElement).value as any, page: 1 })}
          >
            <option value="DUBAI">Dubai</option>
            <option value="DOHA">Doha</option>
            <option value="ALL">All Locations</option>
          </select>
          {user?.location && filters.location === user.location && (
            <span className="location-badge default-badge" title="Your registered location">
              📍 Default
            </span>
          )}
          {user?.location && filters.location !== user.location && (
            <span className="location-badge custom-badge" title={`Your registered location is ${user.location}`}>
              ⚠️ Custom Filter
            </span>
          )}
        </div>
        
        <div className="filter-group">
          <label>Sort:</label>
          <select
            value={filters.sortOrder}
            onChange={(e) => setFilters({ ...filters, sortOrder: (e.target as HTMLSelectElement).value as any, page: 1 })}
          >
            <option value="DESC">Newest First</option>
            <option value="ASC">Oldest First</option>
          </select>
        </div>
        
        <div className="filter-group search-group">
          <label>Search Certificate ID:</label>
          <input
            type="text"
            placeholder="Enter certificate number..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: (e.target as HTMLInputElement).value, page: 1 })}
          />
        </div>
      </div>
      
      {/* Loading/Error States */}
      {loading && <div className="loading-state">Loading certificates...</div>}
      {error && <div className="error-state">Error: {error}</div>}
      
      {/* Table */}
      {!loading && !error && (
        <>
          <div className="table-wrapper">
            <table className="certificate-table">
              <thead>
                <tr>
                  {visibleColumns.map(col => (
                    <th key={col.key} className={getCellClass(col.key)}>
                      {col.key === 'select' ? (
                        <div 
                          className="checkbox-wrapper"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleAllCertificates();
                          }}
                          title={isAllSelected ? 'Deselect all' : 'Select all'}
                        >
                          <input
                            type="checkbox"
                            checked={isAllSelected}
                            readOnly
                            className="cert-checkbox"
                          />
                        </div>
                      ) : (
                        col.label
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {certificates.length === 0 ? (
                  <tr>
                    <td colSpan={visibleColumns.length} className="no-data">
                      No certificates found
                    </td>
                  </tr>
                ) : (
                  certificates.map((cert, index) => (
                    <tr key={cert.cert_id}>
                      {visibleColumns.map(col => (
                        <td key={col.key} className={getCellClass(col.key)}>
                          {renderCell(col.key, cert, index)}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          <div className="pagination">
            <div className="pagination-info">
              Showing {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} certificates
            </div>
            <div className="pagination-controls">
              <button
                disabled={pagination.page === 1}
                onClick={() => setFilters({ ...filters, page: pagination.page - 1 })}
              >
                ← Previous
              </button>
              <span>Page {pagination.page} of {pagination.totalPages}</span>
              <button
                disabled={pagination.page === pagination.totalPages}
                onClick={() => setFilters({ ...filters, page: pagination.page + 1 })}
              >
                Next →
              </button>
            </div>
          </div>
        </>
      )}
      
      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title="Delete Certificate"
        message={`Are you sure you want to delete certificate "${deleteConfirm.cert?.cert_id}"?`}
        details={deleteConfirm.cert ? [
          `Template: ${deleteConfirm.cert.template_name}`,
          `Created: ${formatDate(deleteConfirm.cert.created_at)}`,
          `User: ${deleteConfirm.cert.username}`,
          `Location: ${deleteConfirm.cert.location}`,
          '',
          '⚠️ This action cannot be undone!',
          '⚠️ All related data (gemstones, images) will be permanently deleted.'
        ] : []}
        confirmText="Delete Certificate"
        cancelText="Cancel"
        onConfirm={() => {
          if (deleteConfirm.cert) {
            handleDelete(deleteConfirm.cert);
          }
        }}
        onCancel={() => setDeleteConfirm({ isOpen: false, cert: null })}
        type="danger"
      />
      
      {/* Zebra ODBC Publish Preview Modal */}
      {showZebraPreview && (
        <div className="modal-overlay" onClick={cancelPublishToZebra}>
          <div className="modal-content zebra-preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>🖨️ Publish to ODBC - Preview</h2>
              <button className="modal-close" onClick={cancelPublishToZebra}>✕</button>
            </div>
            
            <div className="modal-body">
              <div className="preview-info">
                <p><strong>You are about to publish to: 📍 {targetPublishLocation}</strong></p>
                <ul className="preview-stats">
                  <li>📊 <strong>{selectedCertificates.size}</strong> certificate{selectedCertificates.size !== 1 ? 's' : ''}</li>
                  <li>📋 <strong>{columns.filter(col => col.enabled && col.key !== 'select' && col.key !== 'actions').length}</strong> columns</li>
                </ul>
                
                <div className="preview-certificates">
                  <p><strong>Certificates:</strong></p>
                  <div className="cert-list-preview">
                    {certificates
                      .filter(cert => selectedCertificates.has(cert.cert_id))
                      .slice(0, 10)
                      .map(cert => (
                        <div key={cert.cert_id} className="cert-preview-item">
                          ✓ {cert.cert_id} - {cert.metal_composition} - {cert.item_gross_weight}g
                        </div>
                      ))}
                    {selectedCertificates.size > 10 && (
                      <div className="cert-preview-more">
                        ... and {selectedCertificates.size - 10} more
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="preview-warning">
                  <p>⚠️ <strong>This will replace existing ODBC data</strong></p>
                  <p className="warning-detail">
                    The current staging table will be cleared and replaced with the selected certificates.
                    Zebra Print Studio will see this new data immediately.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn-secondary" 
                onClick={cancelPublishToZebra}
                disabled={zebraPublishing}
              >
                Cancel
              </button>
              <button 
                className="btn-primary btn-zebra" 
                onClick={confirmPublishToZebra}
                disabled={zebraPublishing}
              >
                {zebraPublishing ? '⏳ Publishing...' : '🖨️ Publish Now'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Excel Export Password Prompt Modal */}
      {showExcelPasswordPrompt && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '450px' }}>
            <div className="modal-header">
              <h3>🔒 Excel Export Password</h3>
            </div>
            
            <div className="modal-body">
              <p style={{ marginBottom: '20px', color: '#94a3b8' }}>
                Enter password to export {selectedCertificates.size} certificate(s) to Excel:
              </p>
              
              <div className="form-field">
                <label htmlFor="excel-password">Password</label>
                <input
                  id="excel-password"
                  type="password"
                  value={excelPassword}
                  onChange={(e) => {
                    setExcelPassword(e.target.value);
                    setExcelPasswordError('');
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      performExcelExport();
                    }
                  }}
                  placeholder="Enter export password"
                  autoFocus
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '6px',
                    border: '1px solid rgba(212, 175, 55, 0.3)',
                    background: 'rgba(15, 23, 42, 0.6)',
                    color: '#e2e8f0',
                    fontSize: '16px'
                  }}
                />
              </div>
              
              {excelPasswordError && (
                <div style={{
                  marginTop: '12px',
                  padding: '10px',
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: '6px',
                  color: '#fca5a5'
                }}>
                  {excelPasswordError}
                </div>
              )}
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn-secondary" 
                onClick={cancelExcelExport}
              >
                Cancel
              </button>
              <button 
                className="btn-primary" 
                onClick={performExcelExport}
                disabled={!excelPassword.trim()}
              >
                📊 Export to Excel
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Column Selector Password Prompt Modal */}
      {showColumnSelectorPasswordPrompt && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '450px' }}>
            <div className="modal-header">
              <h3>🔒 Column Selector Password</h3>
            </div>
            
            <div className="modal-body">
              <p style={{ marginBottom: '20px', color: '#94a3b8' }}>
                Enter password to access the column selector:
              </p>
              
              <div className="form-field">
                <label htmlFor="selector-password">Password</label>
                <input
                  id="selector-password"
                  type="password"
                  value={columnSelectorPassword}
                  onChange={(e) => {
                    setColumnSelectorPassword(e.target.value);
                    setColumnSelectorPasswordError('');
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      openColumnSelector();
                    }
                  }}
                  placeholder="Enter selector password"
                  autoFocus
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '6px',
                    border: '1px solid rgba(212, 175, 55, 0.3)',
                    background: 'rgba(15, 23, 42, 0.6)',
                    color: '#e2e8f0',
                    fontSize: '16px'
                  }}
                />
              </div>
              
              {columnSelectorPasswordError && (
                <div style={{
                  marginTop: '12px',
                  padding: '10px',
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: '6px',
                  color: '#fca5a5'
                }}>
                  {columnSelectorPasswordError}
                </div>
              )}
            </div>
            
            <div className="modal-footer">
              <button 
                className="btn-secondary" 
                onClick={cancelColumnSelector}
              >
                Cancel
              </button>
              <button 
                className="btn-primary" 
                onClick={openColumnSelector}
                disabled={!columnSelectorPassword.trim()}
              >
                📋 Open Column Selector
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Toast Notification */}
      {zebraToast.show && (
        <div className={`toast-notification toast-${zebraToast.type}`}>
          <span className="toast-message">{zebraToast.message}</span>
          <button 
            className="toast-close" 
            onClick={() => setZebraToast({ show: false, message: '', type: 'success' })}
          >
            ✕
          </button>
        </div>
      )}
      
    </div>
  );
};


export default CertificateList;

